import { useState, useCallback } from 'react';
import { unionBy, concat } from 'lodash';
import { useIntl, defineMessages } from 'react-intl';

import apiClient from 'helpers/api-client';
import { useModalToast } from 'components/shared/DesignSystem/ModalToast/useModalToast';

const IMAGE_TYPE = 'Image';
const VIDEO_TYPE = 'Video';

const messages = defineMessages({
  restoreSuccessMessage: {
    id: 'Project.trashCan.restoreSuccessMessage',
    defaultMessage:
      '{count, plural, one {# Photo} other {# Photos}} Moved Back to Project',
    description: 'success message for restoring photos',
  },
  restoreFailureMessage: {
    id: 'Project.trashCan.restoreFailureMessage',
    defaultMessage: 'There was an error. Your photos have not been restored',
    description: 'failure message for restoring photos',
  },
});

export const useTrashAssets = ({ projectId }) => {
  const [deletedAssets, setDeletedAssets] = useState([]);
  const [fetchDeletedAssetsLoading, setFetchDeletedAssetsLoading] = useState(
    false,
  );
  const [
    restoreDeletedAssetsLoading,
    setRestoreDeletedAssetsLoading,
  ] = useState(false);
  const [error, setError] = useState();
  const intl = useIntl();
  const { showToast } = useModalToast();

  const typedAssets = (assets, type) => {
    return assets.map((asset) => {
      return {
        ...asset,
        type,
      };
    });
  };

  const fetchDeletedAssets = useCallback(
    async () => {
      setFetchDeletedAssetsLoading(true);
      try {
        const fetchImagesData = await apiClient.get(
          `locations/${projectId}/trashcan/images`,
        );
        const images = typedAssets(fetchImagesData, IMAGE_TYPE);
        const fetchVideosData = await apiClient.get(
          `locations/${projectId}/trashcan/videos`,
        );
        const videos = typedAssets(fetchVideosData, VIDEO_TYPE);
        const mergedDeletedAssets = unionBy(
          images,
          videos,
          (asset) => `${asset.type}:${asset.id}`,
        );
        setFetchDeletedAssetsLoading(false);
        setDeletedAssets(mergedDeletedAssets);
      } catch (assetFetchError) {
        setFetchDeletedAssetsLoading(false);
        setError(assetFetchError);
      }
    },
    [projectId],
  );

  const restoreDeletedAssets = (assets) => {
    setRestoreDeletedAssetsLoading(true);
    Promise.all([
      apiClient.put(`locations/${projectId}/trashcan/images/restore`, {
        image_ids: assets
          .filter((el) => el.type === IMAGE_TYPE)
          .map((a) => a.id),
      }),
      apiClient.put(`locations/${projectId}/trashcan/videos/restore`, {
        video_ids: assets
          .filter((el) => el.type === VIDEO_TYPE)
          .map((a) => a.id),
      }),
    ])
      .then((values) => {
        const restoredCount = concat(...values).filter(
          (el) => el.status === 'Restored',
        ).length;
        showToast({
          type: 'success',
          message: intl.formatMessage(messages.restoreSuccessMessage, {
            count: restoredCount,
          }),
        });
        setRestoreDeletedAssetsLoading(false);
        fetchDeletedAssets();
      })
      .catch((_restoreError) => {
        setRestoreDeletedAssetsLoading(false);
        showToast({
          type: 'error',
          message: intl.formatMessage(messages.restoreFailureMessage),
        });
      });
  };

  return {
    deletedAssets,
    fetchDeletedAssetsLoading,
    restoreDeletedAssetsLoading,
    error,
    fetchDeletedAssets,
    restoreDeletedAssets,
    IMAGE_TYPE,
    VIDEO_TYPE,
  };
};

export default useTrashAssets;
